import { getType, setValue } from '.'
import { CONTENT_SOURCE, RICHTEXT_TYPE, VARIABLE_TYPE } from './constants/content'
import { isAsset, isEntry, isRichTextPart } from './helper'
import assetTypesMapping from './mappings/asset-types'
import contentTypesMapping from './mappings/content-types'
import { skipFormattingFields } from './settings'
import { validateCustomData } from './validation'

const getComponentSlots = entry => {
  const slots = contentTypesMapping[entry.contentType]?.slots

  return typeof slots === 'function'
    ? slots(entry)
    : slots
}

const getComponentTransformation = entry => contentTypesMapping[entry?.contentType]?.transform

const setComponentType = (entry, source) => {
  if (!entry) return

  const componentType = isAsset(entry)
    ? assetTypesMapping[entry.assetType.split('/')[0]]?.componentType
    : contentTypesMapping[entry.contentType]?.componentType

  entry.componentType = typeof componentType === 'function'
    ? componentType(entry, source)
    : componentType
}

const transformFields = (fields, source, site) => {
  const transform = getComponentTransformation(fields)
  transform?.(fields, source, site)
}

const formatField = (field, site) => {
  const fieldsArr = Array.isArray(field) ? field : [field]

  for (const fieldItem of fieldsArr) {
    let source
    let fieldValue

    if (isEntry(fieldItem)) {
      source = CONTENT_SOURCE.FIELD
      fieldValue = fieldItem
    } else if (isRichTextPart(fieldItem) && fieldItem.type !== RICHTEXT_TYPE.TEXT) {
      source = CONTENT_SOURCE.RICHTEXT
      fieldValue = fieldItem.value
    }

    if (source) {
      setComponentType(fieldValue, source)
      transformFields(fieldValue, source, site)
    }

    for (const subField of Object.values(fieldValue || {})) {
      formatField(subField, site)
    }
  }
}

const preProcessCustomData = (data) => {
  const processItem = (item) => {
    if (typeof item === 'function') return item()
    else if (Array.isArray(item)) return item.map(processItem)
    else if (item && typeof item === 'object') { return Object.fromEntries(Object.entries(item).map(([key, value]) => [key, processItem(value)])) }

    return item
  }

  return processItem(data)
}

const mergeCustomData = (page, customData) => {
  validateCustomData(customData)
  const preProcessedCustomData = preProcessCustomData(customData)

  for (const entryData of preProcessedCustomData) {
    if (!entryData?.path || !entryData.data) continue

    let nestedValue = entryData.path
      .split('.')
      .reduce((obj, prop) => obj[prop], page)

    if (getType(nestedValue) === getType(entryData.data)) {
      if (getType(nestedValue) === VARIABLE_TYPE.OBJECT && entryData.merge === true) {
        nestedValue = { ...nestedValue, ...entryData.data }
      } else {
        nestedValue = entryData.data
      }
    } else if (getType(nestedValue) === VARIABLE_TYPE.ARRAY) {
      let position = getType(entryData.position) === VARIABLE_TYPE.NUMBER ? entryData.position : null

      if (entryData.replace === true || entryData.merge === true) {
        position = nestedValue.findIndex(section => section?.contentId === entryData.data.contentId)
      }

      if (getType(position) === VARIABLE_TYPE.NUMBER && position >= 0) {
        if (entryData.merge === true) {
          nestedValue[position] = { ...nestedValue[position], ...entryData.data }
        } else {
          nestedValue.splice(position, Number(entryData.replace === true), entryData.data)
        }
      }
    }

    setValue(page, entryData.path, nestedValue)
  }
}

const getFormattedPage = (page, customData = [], site) => {
  mergeCustomData(page, customData)

  for (const [fieldName, fieldValue] of Object.entries(page.content)) {
    if (!skipFormattingFields.includes(fieldName)) {
      formatField(fieldValue, site)
    }
  }

  return page
}

export {
  getComponentSlots,
  getFormattedPage,
  formatField,
  setComponentType,
  transformFields
}
